import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackCta } from '../analytics';
import { ScrollReveal } from './ScrollReveal';
import ScrollCue from './ScrollCue';
import { SHOWCASE_DEMOS } from './demos/demoRegistry';
import BookDemoButton from './BookDemoButton';
import './demos/marketingDemos.css';
import './HomeSections.css';

const SHOWCASE_SECTIONS = [
  {
    id: 'workflow',
    eyebrow: 'End to end',
    title: 'End to end workflow, from planning to published',
    body:
      'A single deliverable moves through planning, library media, drafts, and publish. Each stage builds on what was approved before it.',
    accent: 'blue',
    gradient: 0,
  },
  {
    id: 'workspaces',
    eyebrow: 'Client workspaces',
    title: 'Easily switch between clients',
    body:
      'Open the workspace dropdown and jump between clients. Each library shows only that client’s media, drafts, and pipeline.',
    accent: 'indigo',
    gradient: 1,
  },
  {
    id: 'platforms',
    eyebrow: 'Multi-platform publishing',
    title: 'Map drafts to every platform you publish on',
    body:
      'Turn one asset into platform-specific drafts for YouTube, Instagram, TikTok, LinkedIn, and more, without duplicating your library.',
    accent: 'teal',
    gradient: 2,
  },
  {
    id: 'pipeline',
    eyebrow: 'Approval process',
    title: 'Every deliverable follows its own approval path',
    body:
      'All aspects of your media, from planning to publishing, is handled here. Planning, library files, and drafts each track internal review, changes requested, client review, and approved — never mixed in one list.',
    accent: 'indigo',
    gradient: 5,
  },
  {
    id: 'review',
    eyebrow: 'Review process',
    title: 'Review media at the exact moment',
    body:
      'Leave notes tied to playback time, approve or request changes, and keep feedback on the asset, not buried in email threads.',
    accent: 'teal',
    gradient: 3,
  },
  {
    id: 'revisions',
    eyebrow: 'Revisions',
    title: 'Request changes, then spin up the next revision',
    body:
      'When a cut is not ready, reviewers request changes on the asset. Editors start a new revision without losing feedback or guessing which file to open.',
    accent: 'blue',
    gradient: 6,
  },
  {
    id: 'team',
    eyebrow: 'Workspace roles',
    title: 'Invite your team with the right access',
    body:
      'Owners, managers, editors, producers, and clients each get permissions that match how agencies work — including limited visibility for client reviewers, without sharing passwords.',
    accent: 'indigo',
    gradient: 4,
  },
];

function ShowcaseDemo({ sectionId }) {
  const Demo = SHOWCASE_DEMOS[sectionId];
  if (!Demo) return null;
  return <Demo />;
}

const HomeSections = () => {
  const reduceMotion = useReducedMotion();
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const appBaseUrl = process.env.REACT_APP_APP_URL || 'https://app.pycrust.io';
  const signupUrl = `${appBaseUrl}/login`;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 48rem)');
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);
    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  const shouldAnimateAmbient = !reduceMotion && !isMobileViewport;

  return (
    <div className="home-sections">
      {SHOWCASE_SECTIONS.map((section, index) => {
        const reverseLayout = index % 2 === 1;
        const nextSection = SHOWCASE_SECTIONS[index + 1];
        const nextTargetId = nextSection ? nextSection.id : 'get-started';
        const nextScrollLabel = nextSection ? nextSection.eyebrow : 'Get started';

        return (
        <section
          key={section.id}
          id={section.id}
          className={`home-showcase home-showcase--accent-${section.accent} home-showcase--gradient-${section.gradient}${
            index === 0 ? ' home-showcase--peek' : ''
          }${
            section.id === 'workflow' || section.id === 'platforms' || section.id === 'pipeline'
              ? ' home-showcase--overflow-visible'
              : ''
          }${section.id === 'pipeline' ? ' home-showcase--pipeline' : ''}`}
          aria-labelledby={`${section.id}-title`}
        >
          <motion.div
            className="home-showcase-ambient"
            aria-hidden
            animate={
              shouldAnimateAmbient
                ? {
                    x: [0, index % 2 === 0 ? 12 : -12, 0],
                    y: [0, -8, 0],
                  }
                : undefined
            }
            transition={{ duration: 14 + index * 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="home-showcase-glow" aria-hidden />

          <div
            className={`home-showcase-inner home-showcase-inner--split${
              reverseLayout ? ' home-showcase-inner--reverse' : ''
            }`}
          >
            <ScrollReveal
              direction={reverseLayout ? 'left' : 'right'}
              className="home-showcase-copy"
              entrance="fade"
            >
              <p className="home-showcase-eyebrow">{section.eyebrow}</p>
              <h2 id={`${section.id}-title`} className="home-showcase-title">
                {section.title}
              </h2>
              <p className="home-showcase-body">{section.body}</p>
            </ScrollReveal>
            <div className="home-showcase-demo">
              <ShowcaseDemo sectionId={section.id} />
            </div>
          </div>

          <ScrollCue
            variant="section"
            sectionId={section.id}
            targetId={nextTargetId}
            label="See more"
            ariaLabel={`See more: ${nextScrollLabel}`}
          />
        </section>
        );
      })}

      <section
        id="get-started"
        className="home-showcase home-cta home-cta--showcase home-cta--light home-showcase--accent-indigo home-showcase--gradient-cta"
        aria-labelledby="home-cta-title"
      >
        <motion.div
          className="home-showcase-ambient"
          aria-hidden
          animate={
            shouldAnimateAmbient
              ? {
                  x: [0, -10, 0],
                  y: [0, 8, 0],
                }
              : undefined
          }
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="home-showcase-glow" aria-hidden />
        <div className="home-cta-split home-showcase-inner home-showcase-inner--split home-showcase-inner--reverse">
          <ScrollReveal direction="left" className="home-showcase-copy" entrance="fade">
            <p className="home-showcase-eyebrow">Free for 30 days</p>
            <h2 id="home-cta-title" className="home-showcase-title">
              Start running your agency on Pycrust.
            </h2>
            <p className="home-showcase-body home-showcase-body--cta">
              Give every client their own space. Plan content, collect approvals, and publish—without
              chasing the latest version through email.
            </p>
            <div className="home-cta-actions home-cta-actions--inline cta-actions-row cta-actions-row--start">
              <motion.button
                type="button"
                className="btn btn-primary home-cta-button"
                aria-label="Start free for 30 days"
                whileHover={reduceMotion ? undefined : { scale: 1.04, y: -2 }}
                whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                onClick={() => {
                  trackCta('get_started', 'home_cta');
                  window.open(signupUrl, '_blank');
                }}
              >
                <span className="home-cta-button-label home-cta-button-label--desktop">
                  Start free for 30 days
                </span>
                <span className="home-cta-button-label home-cta-button-label--mobile">
                  Start free
                </span>
                <ArrowRight className="ui-icon-2xl" aria-hidden />
              </motion.button>
              <BookDemoButton placement="home_cta" className="home-cta-button" />
              <Link
                to="/contact"
                className="home-cta-link home-cta-link--desktop-only"
                onClick={() => trackCta('contact', 'home_cta')}
              >
                Contact us
              </Link>
            </div>
          </ScrollReveal>
          <div className="home-showcase-demo home-showcase-demo--cta">
            <ShowcaseDemo sectionId="agency-control" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeSections;
